import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Calendar as CalendarIcon,
    LogOut,
    Plus,
    Trash2,
    Loader2,
    RefreshCcw,
    Zap,
    Settings as SettingsIcon,
    Save,
    Pencil,
    Sun,
    Moon,
    X,
    Download,
    Mail,
    Users,
    Check,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminApi,
    setAdminPassword,
    clearAdminPassword,
    getAdminPassword,
    api,
} from "@/lib/api";
import { Logo, SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";

export default function Admin() {
    const [authed, setAuthed] = useState(Boolean(getAdminPassword()));
    return authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
    ) : (
        <Login onAuthed={() => setAuthed(true)} />
    );
}

function Login({ onAuthed }) {
    const site = useSiteInfo();
    const [username, setUsername] = useState("");
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/admin/login", { username, password: pw });
            setAdminPassword(pw);
            onAuthed();
        } catch {
            toast.error("Ongeldige inloggegevens");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-12">
            <aside className="lg:col-span-5 bg-[#0A1F44] text-white p-10 md:p-16 flex flex-col">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                    data-testid="back-home"
                >
                    ← Terug naar website
                </Link>
                <div className="mt-12 flex flex-col gap-4 items-start">
                    <Logo tone="light" subtitle="ADMIN" />
                    <SiteTag name={site.name} city={site.city} tone="light" />
                </div>
                <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-12 leading-[1.05]">
                    Beheer voor
                    <br />
                    <span className="text-[#ED6A14]">het team.</span>
                </h1>
                <p className="text-white/60 mt-6 max-w-sm">
                    Publiceer wasdagen, bekijk boekingen en houd de planning op
                    {" "}
                    {site.name} op schema.
                </p>
            </aside>
            <main className="lg:col-span-7 p-10 md:p-16 flex items-center">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md"
                    data-testid="admin-login-form"
                >
                    <p className="text-xs uppercase tracking-widest text-[#555]">
                        Aanmelden
                    </p>
                    <h2 className="font-display font-bold text-3xl tracking-tight mt-2 text-[#0A1F44]">
                        Admin login
                    </h2>
                    <div className="mt-8">
                        <label className="text-xs uppercase tracking-widest text-[#555]">
                            Gebruikersnaam
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="naam@cargidesgroup"
                            className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                            data-testid="admin-username-input"
                            autoFocus
                            autoComplete="username"
                        />
                    </div>
                    <div className="mt-8">
                        <label className="text-xs uppercase tracking-widest text-[#555]">
                            Wachtwoord
                        </label>
                        <input
                            type="password"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="••••••••"
                            className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                            data-testid="admin-password-input"
                            autoComplete="current-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || !username || !pw}
                        className="mt-10 inline-flex items-center gap-2 bg-[#ED6A14] text-white px-7 py-3.5 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                        data-testid="admin-login-btn"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin" size={16} />
                        ) : null}{" "}
                        Naar dashboard
                    </button>
                </form>
            </main>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const site = useSiteInfo();
    const [days, setDays] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [newDate, setNewDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [quickLoading, setQuickLoading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [editing, setEditing] = useState(null); // booking object or null
    const [marketing, setMarketing] = useState([]);
    const [newMarketingEmail, setNewMarketingEmail] = useState("");
    const [savingMarketing, setSavingMarketing] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [d, b, s, m] = await Promise.all([
                adminApi.get("/admin/days"),
                adminApi.get("/admin/bookings"),
                adminApi.get("/admin/settings"),
                adminApi.get("/admin/marketing-recipients"),
            ]);
            setDays(d.data);
            setBookings(b.data);
            setSettings(s.data);
            setMarketing(m.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminPassword();
                onLogout();
                return;
            }
            toast.error("Laden mislukt");
        } finally {
            setLoading(false);
        }
    };

    const addMarketingRecipient = async (e) => {
        e.preventDefault();
        const email = newMarketingEmail.trim();
        if (!email) return;
        setSavingMarketing(true);
        try {
            await adminApi.post("/admin/marketing-recipients", { email });
            setNewMarketingEmail("");
            const m = await adminApi.get("/admin/marketing-recipients");
            setMarketing(m.data);
            toast.success("E-mail toegevoegd aan mailinglijst");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail?.[0]?.msg
                || err?.response?.data?.detail
                || "Toevoegen mislukt (controleer e-mailformaat)",
            );
        } finally {
            setSavingMarketing(false);
        }
    };

    const removeMarketingRecipient = async (rid, email) => {
        if (!window.confirm(`E-mail "${email}" verwijderen uit mailinglijst?`)) return;
        try {
            await adminApi.delete(`/admin/marketing-recipients/${rid}`);
            setMarketing((prev) => prev.filter((r) => r.id !== rid));
            toast.success("E-mail verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        if (!settings) return;
        setSavingSettings(true);
        try {
            const res = await adminApi.put("/admin/settings", {
                site_name: settings.site_name,
                site_city: settings.site_city,
                price_exterior: parseFloat(settings.price_exterior),
                price_interior_exterior: parseFloat(
                    settings.price_interior_exterior,
                ),
                duration_exterior: parseInt(settings.duration_exterior, 10),
                duration_interior_exterior: parseInt(
                    settings.duration_interior_exterior,
                    10,
                ),
            });
            setSettings(res.data);
            toast.success("Park-instellingen opgeslagen");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Opslaan mislukt",
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const deleteBooking = async (b) => {
        const ok = window.confirm(
            `Boeking voor ${b.license_plate} op ${b.date} verwijderen? Dit kan niet ongedaan worden gemaakt.`,
        );
        if (!ok) return;
        try {
            await adminApi.delete(`/admin/bookings/${b.id}`);
            toast.success("Boeking verwijderd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Verwijderen mislukt",
            );
        }
    };

    const grouped = useMemo(() => groupBookings(bookings, days), [bookings, days]);

    const exportDay = async (date) => {
        try {
            const res = await adminApi.get(
                `/admin/bookings/export?date=${date}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `carwash-${date}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`CSV gedownload voor ${date}`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "CSV-export mislukt",
            );
        }
    };

    const [sendingCrewMail, setSendingCrewMail] = useState(false);
    const sendCrewMailNow = async () => {
        if (sendingCrewMail) return;
        setSendingCrewMail(true);
        try {
            const res = await adminApi.post("/admin/crew-csv/send-now");
            const data = res.data || {};
            if (data.sent) {
                toast.success(
                    `Mail verstuurd naar ${data.to} (${data.bookings} boeking${
                        data.bookings === 1 ? "" : "en"
                    } voor ${data.date})`,
                );
            } else {
                const reasons = {
                    no_api_key: "RESEND_API_KEY ontbreekt.",
                    no_crew_email: "CREW_EMAIL ontbreekt in .env.",
                    no_upcoming_day: "Geen aankomende wasdag gevonden.",
                    send_failed: "Versturen mislukt: " + (data.error || ""),
                };
                toast.error(reasons[data.reason] || "Mail niet verstuurd.");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Crew-mail mislukt",
            );
        } finally {
            setSendingCrewMail(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line
    }, []);

    const addDay = async (e) => {
        e.preventDefault();
        if (!newDate) return;
        setSubmitting(true);
        try {
            await adminApi.post("/admin/days", { date: newDate });
            setNewDate("");
            toast.success("Wasdag gepubliceerd");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon dag niet toevoegen");
        } finally {
            setSubmitting(false);
        }
    };

    const deleteDay = async (id) => {
        if (!window.confirm("Deze wasdag verwijderen?")) return;
        try {
            await adminApi.delete(`/admin/days/${id}`);
            toast.success("Wasdag verwijderd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kon dag niet verwijderen",
            );
        }
    };

    const quickAddDates = async (dates, label) => {
        if (dates.length === 0) return;
        setQuickLoading(true);
        try {
            const res = await adminApi.post("/admin/days/bulk", { dates });
            toast.success(`${label}: ${res.data.length} dag(en) gepubliceerd`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon dagen niet toevoegen");
        } finally {
            setQuickLoading(false);
        }
    };

    const addTomorrow = () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        quickAddDates([d.toISOString().slice(0, 10)], "Morgen");
    };

    const addNextWeekdays = (count, label) => {
        const dates = [];
        const d = new Date();
        d.setDate(d.getDate() + 1);
        while (dates.length < count) {
            const dow = d.getDay(); // 0=Sun, 6=Sat
            if (dow !== 0 && dow !== 6) {
                dates.push(d.toISOString().slice(0, 10));
            }
            d.setDate(d.getDate() + 1);
        }
        quickAddDates(dates, label);
    };

    const logout = () => {
        clearAdminPassword();
        onLogout();
    };

    const confirmedCount = bookings.filter(
        (b) => b.status === "confirmed",
    ).length;
    const revenue = bookings
        .filter((b) => b.status === "confirmed")
        .reduce((s, b) => s + (b.amount_eur || 0), 0);

    return (
        <div className="min-h-screen grid lg:grid-cols-12">
            <aside className="lg:col-span-2 bg-[#0A1F44] text-white p-6 md:p-8 flex lg:flex-col justify-between">
                <div>
                    <Link
                        to="/"
                        className="block"
                        data-testid="brand-link"
                    >
                        <Logo tone="light" subtitle="ADMIN" />
                    </Link>
                    <nav className="mt-10 hidden lg:flex flex-col gap-1 text-sm">
                        <span className="px-3 py-2 bg-[#ED6A14] text-white font-semibold">
                            Dashboard
                        </span>
                    </nav>
                </div>
                <button
                    onClick={logout}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                    data-testid="admin-logout-btn"
                >
                    <LogOut size={14} /> Afmelden
                </button>
            </aside>

            <main className="lg:col-span-10 p-6 md:p-10 lg:p-14">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="mb-3">
                            <SiteTag name={site.name} city={site.city} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-[#555]">
                            Overzicht
                        </p>
                        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-2 text-[#0A1F44]">
                            Dashboard
                        </h1>
                    </div>
                    <button
                        onClick={refresh}
                        className="inline-flex items-center gap-2 border border-[#e5e5e5] px-4 py-2 text-sm hover:border-[#0A1F44] transition-colors text-[#0A1F44]"
                        data-testid="admin-refresh-btn"
                    >
                        <RefreshCcw size={14} /> Vernieuwen
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e5e5e5] border border-[#e5e5e5] mb-12">
                    <Kpi
                        label="Actieve wasdagen"
                        value={days.length}
                        testId="kpi-days"
                    />
                    <Kpi
                        label="Bevestigde boekingen"
                        value={confirmedCount}
                        testId="kpi-confirmed"
                    />
                    <Kpi
                        label="Omzet (betaald)"
                        value={`€${revenue.toFixed(2).replace(".", ",")}`}
                        testId="kpi-revenue"
                    />
                </div>

                {/* Park settings */}
                <section className="mb-12" data-testid="park-settings-section">
                    <div className="flex items-center gap-3 mb-2">
                        <SettingsIcon
                            size={20}
                            className="text-[#ED6A14]"
                        />
                        <h2 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                            Park-instellingen
                        </h2>
                    </div>
                    <p className="text-sm text-[#555] mb-6">
                        Pas hier de naam, stad en prijzen aan. Wijzigingen zijn
                        meteen zichtbaar op de publieke site — handig om dit
                        platform te dupliceren voor een ander bedrijvenpark.
                    </p>
                    {settings ? (
                        <form
                            onSubmit={saveSettings}
                            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 max-w-3xl border border-[#e5e5e5] p-6"
                            data-testid="park-settings-form"
                        >
                            <Field
                                label="Park-naam"
                                value={settings.site_name}
                                onChange={(v) =>
                                    setSettings({ ...settings, site_name: v })
                                }
                                testId="setting-name"
                            />
                            <Field
                                label="Stad"
                                value={settings.site_city}
                                onChange={(v) =>
                                    setSettings({ ...settings, site_city: v })
                                }
                                testId="setting-city"
                            />
                            <Field
                                label="Prijs Exterieur (€, incl. btw)"
                                type="number"
                                step="0.01"
                                value={settings.price_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        price_exterior: v,
                                    })
                                }
                                testId="setting-price-exterior"
                            />
                            <Field
                                label="Prijs Interieur & Exterieur (€, incl. btw)"
                                type="number"
                                step="0.01"
                                value={settings.price_interior_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        price_interior_exterior: v,
                                    })
                                }
                                testId="setting-price-full"
                            />
                            <Field
                                label="Duur Exterieur (minuten)"
                                type="number"
                                min="5"
                                max="240"
                                step="5"
                                value={settings.duration_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        duration_exterior: v,
                                    })
                                }
                                testId="setting-duration-exterior"
                            />
                            <Field
                                label="Duur Interieur & Exterieur (minuten)"
                                type="number"
                                min="5"
                                max="240"
                                step="5"
                                value={settings.duration_interior_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        duration_interior_exterior: v,
                                    })
                                }
                                testId="setting-duration-full"
                            />
                            <p
                                className="md:col-span-2 text-xs text-[#777] leading-relaxed -mt-2"
                                data-testid="duration-help-text"
                            >
                                Duurtijden zijn van toepassing op <strong>nieuwe</strong> boekingen
                                en wijzigingen. Bestaande boekingen behouden hun oorspronkelijke duur.
                                Capaciteit per dagdeel = poetsers × 240 min.
                            </p>
                            <div className="md:col-span-2 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={savingSettings}
                                    className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                                    data-testid="save-settings-btn"
                                >
                                    {savingSettings ? (
                                        <Loader2
                                            className="animate-spin"
                                            size={16}
                                        />
                                    ) : (
                                        <Save size={16} />
                                    )}{" "}
                                    Instellingen opslaan
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="text-[#555] inline-flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} /> Laden…
                        </div>
                    )}
                </section>

                <section className="mb-12" data-testid="marketing-section">
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-2 text-[#0A1F44]">
                        Herinnerings-mailinglijst
                    </h2>
                    <p className="text-sm text-[#555] mb-6 max-w-3xl">
                        7 dagen voor elke wasdag krijgen deze adressen én alle klanten
                        die eerder al eens geboekt hebben een herinnering met hoeveel
                        plaatsen er nog vrij zijn. Klanten die al geboekt hebben op die
                        dag krijgen een aparte bevestigingsherinnering.
                    </p>

                    <form
                        onSubmit={addMarketingRecipient}
                        className="flex flex-col sm:flex-row gap-3 mb-6 max-w-2xl"
                        data-testid="marketing-add-form"
                    >
                        <input
                            type="email"
                            required
                            placeholder="nieuw-adres@bedrijf.be"
                            value={newMarketingEmail}
                            onChange={(e) => setNewMarketingEmail(e.target.value)}
                            className="flex-1 border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                            data-testid="marketing-new-email-input"
                        />
                        <button
                            type="submit"
                            disabled={savingMarketing || !newMarketingEmail.trim()}
                            className="inline-flex items-center justify-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                            data-testid="marketing-add-btn"
                        >
                            {savingMarketing ? (
                                <Loader2 className="animate-spin" size={16} />
                            ) : (
                                <Plus size={16} />
                            )}{" "}
                            Toevoegen
                        </button>
                    </form>

                    <div
                        className="border border-[#e5e5e5] divide-y divide-[#f0f0f0] max-w-2xl"
                        data-testid="marketing-list"
                    >
                        {marketing.length === 0 ? (
                            <div className="p-4 text-sm text-[#777]">
                                Nog geen adressen in de mailinglijst.
                            </div>
                        ) : (
                            marketing.map((rec) => (
                                <div
                                    key={rec.id}
                                    className="flex items-center justify-between px-4 py-3 gap-4"
                                    data-testid={`marketing-row-${rec.id}`}
                                >
                                    <span className="text-sm text-[#0A1F44] break-all">
                                        {rec.email}
                                    </span>
                                    <button
                                        onClick={() =>
                                            removeMarketingRecipient(rec.id, rec.email)
                                        }
                                        className="text-[#555] hover:text-[#D92D20] transition-colors flex-shrink-0"
                                        title="Verwijder e-mail"
                                        data-testid={`marketing-remove-${rec.id}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                <section className="mb-12">
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-2 text-[#0A1F44]">
                        Wasdag(en) publiceren voor {site.name}
                    </h2>
                    <p className="text-sm text-[#555] mb-6">
                        Open snel boekingsdagen voor het bedrijvenpark. Bestaande
                        dagen worden automatisch overgeslagen.
                    </p>

                    {/* Quick add buttons */}
                    <div className="flex flex-wrap gap-3 mb-8">
                        <button
                            onClick={addTomorrow}
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-tomorrow-btn"
                        >
                            <Zap size={14} /> Morgen
                        </button>
                        <button
                            onClick={() =>
                                addNextWeekdays(5, "Komende 5 werkdagen")
                            }
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-week-btn"
                        >
                            <Zap size={14} /> Komende 5 werkdagen
                        </button>
                        <button
                            onClick={() =>
                                addNextWeekdays(10, "Komende 2 weken")
                            }
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-2weeks-btn"
                        >
                            <Zap size={14} /> Komende 2 weken werkdagen
                        </button>
                        {quickLoading && (
                            <span className="inline-flex items-center gap-2 text-[#555] text-sm">
                                <Loader2
                                    className="animate-spin"
                                    size={14}
                                />{" "}
                                Toevoegen…
                            </span>
                        )}
                    </div>

                    {/* Manual single date add */}
                    <form
                        onSubmit={addDay}
                        className="flex flex-wrap items-end gap-4 pt-6 border-t border-[#e5e5e5]"
                        data-testid="add-day-form"
                    >
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                                Of kies een specifieke datum
                            </label>
                            <input
                                type="date"
                                value={newDate}
                                onChange={(e) => setNewDate(e.target.value)}
                                min={new Date().toISOString().slice(0, 10)}
                                className="border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                data-testid="new-day-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || !newDate}
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                            data-testid="add-day-btn"
                        >
                            <Plus size={16} /> Publiceren
                        </button>
                    </form>
                </section>

                <section className="mb-16">
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-6 text-[#0A1F44]">
                        Gepubliceerde dagen
                    </h2>
                    {loading ? (
                        <div className="text-[#555] inline-flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} />{" "}
                            Laden…
                        </div>
                    ) : days.length === 0 ? (
                        <p className="text-[#555]" data-testid="no-days-msg">
                            Nog geen wasdagen. Publiceer er hierboven een.
                        </p>
                    ) : (
                        <div className="border border-[#e5e5e5] divide-y divide-[#e5e5e5]">
                            {days.map((d) => (
                                <DayRow
                                    key={d.id}
                                    day={d}
                                    onDelete={() => deleteDay(d.id)}
                                    onUpdated={refresh}
                                />
                            ))}
                        </div>
                    )}
                </section>

                <section data-testid="bookings-section">
                    <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                        <h2 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                            Boekingen per dag
                        </h2>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-[#555]">
                                {bookings.length} boeking
                                {bookings.length !== 1 ? "en" : ""} totaal · gesorteerd
                                op datum &amp; tijdslot
                            </span>
                            <button
                                onClick={sendCrewMailNow}
                                disabled={sendingCrewMail}
                                className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-4 py-2 text-sm font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="send-crew-mail-now-btn"
                                title="Stuur de CSV van de volgende wasdag nu naar de wasploeg (loopt automatisch elke avond om 20:00)"
                            >
                                {sendingCrewMail ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Mail size={14} />
                                )}
                                Mail wasploeg nu
                            </button>
                        </div>
                    </div>
                    {bookings.length === 0 ? (
                        <p
                            className="text-[#555]"
                            data-testid="no-bookings-msg"
                        >
                            Nog geen boekingen.
                        </p>
                    ) : (
                        <div className="space-y-10">
                            {grouped.map((day) => (
                                <div
                                    key={day.date}
                                    data-testid={`day-group-${day.date}`}
                                >
                                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                        <div className="flex items-center gap-3">
                                            <CalendarIcon
                                                size={18}
                                                className="text-[#ED6A14]"
                                            />
                                            <h3 className="font-display font-bold text-xl tracking-tight text-[#0A1F44]">
                                                {formatDayHeading(day.date)}
                                            </h3>
                                            <span className="text-xs text-[#555] font-mono-ui">
                                                {day.total} boeking
                                                {day.total !== 1 ? "en" : ""}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => exportDay(day.date)}
                                            className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] px-4 py-2 text-sm font-semibold hover:bg-[#0A1F44] hover:text-white transition-colors"
                                            data-testid={`export-csv-${day.date}`}
                                            title="Download CSV voor de wasploeg"
                                        >
                                            <Download size={14} /> CSV
                                        </button>
                                    </div>
                                    <div className="space-y-6">
                                        {day.morning.length > 0 && (
                                            <SlotSection
                                                slotKey="morning"
                                                bookings={day.morning}
                                                slotTotal={day.morning_total_min}
                                                slotActive={day.morning_active}
                                                onEdit={setEditing}
                                                onDelete={deleteBooking}
                                            />
                                        )}
                                        {day.afternoon.length > 0 && (
                                            <SlotSection
                                                slotKey="afternoon"
                                                bookings={day.afternoon}
                                                slotTotal={day.afternoon_total_min}
                                                slotActive={day.afternoon_active}
                                                onEdit={setEditing}
                                                onDelete={deleteBooking}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
            {editing && (
                <EditBookingModal
                    booking={editing}
                    days={days}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

function SlotSection({ slotKey, bookings, slotTotal, slotActive, onEdit, onDelete }) {
    const isMorning = slotKey === "morning";
    const Icon = isMorning ? Sun : Moon;
    const label = isMorning
        ? "Voormiddag · 08:00 – 12:00"
        : "Namiddag · 13:00 – 17:00";
    const used = bookings.reduce((s, b) => s + serviceDuration(b), 0);
    const total = typeof slotTotal === "number" && slotTotal > 0 ? slotTotal : null;
    const overCapacity = total !== null && used > total;
    const capacityText = slotActive === false
        ? "dagdeel niet actief"
        : total !== null
            ? `${used} / ${total} min gebruikt`
            : `${used} min gebruikt`;
    return (
        <div className="border border-[#e5e5e5]">
            <div
                className={`flex items-center justify-between px-5 py-3 border-b border-[#e5e5e5] ${
                    isMorning ? "bg-[#FFF7F1]" : "bg-[#F2F4F8]"
                }`}
            >
                <div className="flex items-center gap-2.5">
                    <Icon
                        size={16}
                        className={isMorning ? "text-[#ED6A14]" : "text-[#0A1F44]"}
                    />
                    <span className="font-semibold text-[#0A1F44]">
                        {label}
                    </span>
                </div>
                <span
                    className={`text-xs font-mono-ui ${
                        overCapacity ? "text-[#D92D20] font-semibold" : "text-[#555]"
                    }`}
                    data-testid={`slot-usage-${slotKey}`}
                >
                    {bookings.length} wagen{bookings.length !== 1 ? "s" : ""} ·{" "}
                    {capacityText}
                    {overCapacity ? " ⚠" : ""}
                </span>
            </div>
            <div className="overflow-x-auto">
                <table
                    className="w-full text-sm"
                    data-testid={`bookings-table-${slotKey}`}
                >
                    <thead className="bg-[#fcfcfc] text-[#555] uppercase text-[10px] tracking-widest">
                        <tr>
                            <th className="text-left px-4 py-2">Plaat</th>
                            <th className="text-left px-4 py-2">Klant</th>
                            <th className="text-left px-4 py-2">Dienst</th>
                            <th className="text-left px-4 py-2">Btw-nr</th>
                            <th className="text-right px-4 py-2">Bedrag</th>
                            <th className="text-right px-4 py-2">Acties</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookings.map((b) => (
                            <tr
                                key={b.id}
                                className="border-t border-[#eaeaea]"
                                data-testid={`booking-row-${b.id}`}
                            >
                                <td className="px-4 py-3 font-mono-ui font-semibold text-[#0A1F44]">
                                    {b.license_plate}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-[#0A1F44] font-medium">
                                        {b.customer_name || "—"}
                                    </div>
                                    <div className="text-[#555] text-xs">
                                        {b.customer_type === "business"
                                            ? "Bedrijf"
                                            : "Particulier"}{" "}
                                        · {b.customer_email || "—"}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-[#0A1F44]">
                                    {b.service_name}
                                </td>
                                <td className="px-4 py-3 font-mono-ui text-[#555]">
                                    {b.vat_number || "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">
                                    €
                                    {b.amount_eur
                                        .toFixed(2)
                                        .replace(".", ",")}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => onEdit(b)}
                                        className="inline-flex items-center gap-1 text-[#0A1F44] hover:text-[#ED6A14] transition-colors px-2 py-1"
                                        data-testid={`edit-${b.id}`}
                                        title="Wijzig boeking"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={() => onDelete(b)}
                                        className="inline-flex items-center gap-1 text-[#555] hover:text-[#D92D20] transition-colors px-2 py-1 ml-1"
                                        data-testid={`delete-${b.id}`}
                                        title="Verwijder boeking"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EditBookingModal({ booking, days, onClose, onSaved }) {
    const [services, setServices] = useState([]);
    const [form, setForm] = useState({
        customer_type: booking.customer_type || "private",
        customer_name: booking.customer_name || "",
        customer_email: booking.customer_email || "",
        vat_number: booking.vat_number || "",
        service_type: booking.service_type,
        date: booking.date,
        slot: booking.slot,
        license_plate: booking.license_plate,
    });
    const [saving, setSaving] = useState(false);
    const isBusiness = form.customer_type === "business";

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/services`,
                );
                if (res.ok) setServices(await res.json());
            } catch {
                // Silently ignore — dropdown falls back to plain service labels.
            }
        })();
    }, []);

    const serviceLabel = (id, fallbackName) => {
        const svc = services.find((s) => s.id === id);
        if (!svc) return fallbackName;
        const price = svc.price_eur.toFixed(2).replace(".", ",");
        return `${fallbackName} (${svc.duration_min} min · €${price})`;
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                customer_type: form.customer_type,
                customer_name: form.customer_name.trim(),
                customer_email: form.customer_email.trim(),
                vat_number: isBusiness ? form.vat_number.trim() : "",
                service_type: form.service_type,
                date: form.date,
                slot: form.slot,
                license_plate: form.license_plate.trim(),
            };
            await adminApi.put(
                `/admin/bookings/${booking.id}`,
                payload,
            );
            toast.success("Boeking bijgewerkt");
            onSaved();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Opslaan mislukt",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
            data-testid="edit-modal"
            onClick={onClose}
        >
            <div
                className="bg-white border border-[#e5e5e5] w-full max-w-2xl mt-12 mb-12"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[#e5e5e5] px-6 py-4">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-[#555]">
                            Boeking bewerken
                        </p>
                        <h3 className="font-display font-bold text-xl tracking-tight text-[#0A1F44]">
                            {booking.license_plate}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-[#555] hover:text-[#0A1F44] transition-colors"
                        data-testid="edit-modal-close"
                    >
                        <X size={20} />
                    </button>
                </div>
                <form
                    onSubmit={save}
                    className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5"
                    data-testid="edit-form"
                >
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Klant-type
                        </label>
                        <select
                            value={form.customer_type}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_type: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-type"
                        >
                            <option value="private">Particulier</option>
                            <option value="business">Bedrijf</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            {isBusiness ? "Bedrijfsnaam" : "Naam"}
                        </label>
                        <input
                            value={form.customer_name}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_name: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-name"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            E-mail
                        </label>
                        <input
                            type="email"
                            value={form.customer_email}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_email: e.target.value.toLowerCase(),
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-email"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Btw-nummer{" "}
                            {isBusiness && (
                                <span className="text-[#ED6A14]">*</span>
                            )}
                        </label>
                        <input
                            value={form.vat_number}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    vat_number: e.target.value.toUpperCase(),
                                })
                            }
                            disabled={!isBusiness}
                            placeholder={isBusiness ? "BE0123.456.789" : "—"}
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44] disabled:opacity-50 uppercase font-display tracking-wider"
                            data-testid="edit-vat"
                        />
                    </div>
                    <div className="md:col-span-2 border-t border-[#e5e5e5] pt-5" />
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Nummerplaat
                        </label>
                        <input
                            value={form.license_plate}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    license_plate: e.target.value.toUpperCase(),
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44] uppercase font-display tracking-wider"
                            data-testid="edit-plate"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Dienst
                        </label>
                        <select
                            value={form.service_type}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    service_type: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-service"
                        >
                            <option value="exterior">
                                {serviceLabel("exterior", "Exterieur")}
                            </option>
                            <option value="interior_exterior">
                                {serviceLabel(
                                    "interior_exterior",
                                    "Interieur & Exterieur",
                                )}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Datum
                        </label>
                        <select
                            value={form.date}
                            onChange={(e) =>
                                setForm({ ...form, date: e.target.value })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-date"
                        >
                            {/* Always include the booking's current date even if
                                the day was removed afterwards */}
                            {[
                                ...days.map((d) => d.date),
                                ...(days.find((d) => d.date === form.date)
                                    ? []
                                    : [form.date]),
                            ].map((d) => (
                                <option key={d} value={d}>
                                    {new Date(d).toLocaleDateString("nl-BE", {
                                        weekday: "short",
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Tijdslot
                        </label>
                        <select
                            value={form.slot}
                            onChange={(e) =>
                                setForm({ ...form, slot: e.target.value })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-slot"
                        >
                            <option value="morning">
                                Voormiddag (08:00 – 12:00)
                            </option>
                            <option value="afternoon">
                                Namiddag (13:00 – 17:00)
                            </option>
                        </select>
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between border-t border-[#e5e5e5] pt-5 mt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-sm text-[#555] hover:text-[#0A1F44] transition-colors font-semibold"
                            data-testid="edit-cancel-btn"
                        >
                            Annuleren
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                            data-testid="edit-save-btn"
                        >
                            {saving ? (
                                <Loader2 className="animate-spin" size={16} />
                            ) : (
                                <Save size={16} />
                            )}{" "}
                            Wijzigingen opslaan
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function groupBookings(list, dayConfigs = []) {
    const byDate = {};
    for (const b of list) {
        if (!byDate[b.date]) {
            byDate[b.date] = { date: b.date, morning: [], afternoon: [] };
        }
        if (b.slot === "morning") byDate[b.date].morning.push(b);
        else byDate[b.date].afternoon.push(b);
    }
    const cfgByDate = {};
    for (const c of dayConfigs) cfgByDate[c.date] = c;
    return Object.values(byDate)
        .map((d) => {
            const cfg = cfgByDate[d.date] || {};
            return {
                ...d,
                total: d.morning.length + d.afternoon.length,
                morning_total_min: cfg.morning_total_min ?? 0,
                afternoon_total_min: cfg.afternoon_total_min ?? 0,
                morning_active: cfg.morning_active !== false,
                afternoon_active: cfg.afternoon_active !== false,
            };
        })
        .sort((a, b) => a.date.localeCompare(b.date));
}

function formatDayHeading(isoDate) {
    const d = new Date(isoDate);
    return d.toLocaleDateString("nl-BE", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

function serviceDuration(booking) {
    // Prefer the per-booking snapshot from the backend; fall back for very
    // old data that predates the field.
    if (booking && typeof booking.duration_min === "number") {
        return booking.duration_min;
    }
    return booking?.service_type === "interior_exterior" ? 60 : 20;
}

function Kpi({ label, value, testId }) {
    return (
        <div className="bg-white p-6" data-testid={testId}>
            <p className="text-xs uppercase tracking-widest text-[#555]">
                {label}
            </p>
            <p className="font-display font-bold text-4xl tracking-tighter mt-3 text-[#0A1F44]">
                {value}
            </p>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", step, min, max, testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                {label}
            </label>
            <input
                type={type}
                step={step}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                data-testid={testId}
            />
        </div>
    );
}

function StatusBadge({ status, paymentStatus }) {
    let label = status;
    let color = "bg-[#eaeaea] text-[#121212]";
    if (status === "confirmed") {
        label = paymentStatus === "paid" ? "Betaald" : "Bevestigd";
        color =
            paymentStatus === "paid"
                ? "bg-[#00C853]/15 text-[#0A8C3F]"
                : "bg-[#ED6A14]/10 text-[#ED6A14]";
    } else if (status === "reserved") {
        label = "Wacht op bevestiging";
        color = "bg-[#0A1F44]/10 text-[#0A1F44]";
    } else if (status === "cancelled") {
        label = "Geannuleerd";
        color = "bg-[#D92D20]/10 text-[#D92D20]";
    }
    return (
        <span
            className={`inline-block px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${color}`}
        >
            {label}
        </span>
    );
}


// ---------------------------------------------------------------------------
// DayRow — one row in the "Gepubliceerde dagen" list with inline editor
// for poetsers count and half/full-day slot mode.
// ---------------------------------------------------------------------------
function DayRow({ day, onDelete, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [workers, setWorkers] = useState(day.workers_count ?? 2);
    const initialMode = day.morning_active && day.afternoon_active
        ? "full"
        : day.morning_active
            ? "morning_only"
            : "afternoon_only";
    const [slotMode, setSlotMode] = useState(initialMode);

    const slotModeLabel =
        initialMode === "full"
            ? "Volledige dag"
            : initialMode === "morning_only"
                ? "Enkel voormiddag"
                : "Enkel namiddag";

    const startEdit = () => {
        setWorkers(day.workers_count ?? 2);
        setSlotMode(initialMode);
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.patch(`/admin/days/${day.id}`, {
                workers_count: workers,
                slot_mode: slotMode,
            });
            toast.success("Dag bijgewerkt");
            setEditing(false);
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon dag niet opslaan");
        } finally {
            setSaving(false);
        }
    };

    const morningUsed = (day.morning_total_min ?? 0) - (day.morning_remaining_min ?? 0);
    const afternoonUsed = (day.afternoon_total_min ?? 0) - (day.afternoon_remaining_min ?? 0);

    return (
        <div
            className="px-5 py-4 flex flex-col gap-3"
            data-testid={`day-row-${day.date}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-4 min-w-0">
                    <CalendarIcon
                        size={18}
                        className="text-[#ED6A14] mt-1 shrink-0"
                    />
                    <div className="min-w-0">
                        <p className="font-semibold text-[#0A1F44]">
                            {new Date(day.date).toLocaleDateString("nl-BE", {
                                weekday: "short",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            })}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[#555]">
                            <span className="inline-flex items-center gap-1">
                                <Users size={12} />
                                {day.workers_count ?? 2} poetser
                                {(day.workers_count ?? 2) !== 1 ? "s" : ""}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                {initialMode === "morning_only" ? (
                                    <Sun size={12} />
                                ) : initialMode === "afternoon_only" ? (
                                    <Moon size={12} />
                                ) : (
                                    <CalendarIcon size={12} />
                                )}
                                {slotModeLabel}
                            </span>
                        </div>
                        <p className="text-xs text-[#555] mt-1 font-mono-ui">
                            VM{" "}
                            {day.morning_active === false
                                ? "—"
                                : `${morningUsed}/${day.morning_total_min ?? 480}min`}
                            {"  ·  "}
                            NM{" "}
                            {day.afternoon_active === false
                                ? "—"
                                : `${afternoonUsed}/${day.afternoon_total_min ?? 480}min`}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {!editing && (
                        <button
                            onClick={startEdit}
                            className="text-[#555] hover:text-[#0A1F44] transition-colors p-2"
                            title="Capaciteit aanpassen"
                            data-testid={`edit-day-${day.date}`}
                        >
                            <Pencil size={16} />
                        </button>
                    )}
                    <button
                        onClick={onDelete}
                        className="text-[#555] hover:text-[#D92D20] transition-colors p-2"
                        data-testid={`delete-day-${day.date}`}
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>

            {editing && (
                <div
                    className="bg-[#F7F7F5] border border-[#e5e5e5] p-4 flex flex-col gap-4"
                    data-testid={`edit-panel-${day.date}`}
                >
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#0A1F44] mb-2">
                            Aantal poetsers
                        </p>
                        <div className="inline-flex border-2 border-[#e5e5e5] bg-white">
                            {[1, 2, 3, 4].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setWorkers(n)}
                                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                                        workers === n
                                            ? "bg-[#ED6A14] text-white"
                                            : "text-[#0A1F44] hover:bg-[#F7F7F5]"
                                    }`}
                                    data-testid={`workers-${day.date}-${n}`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-[#555] mt-2">
                            ≈ {workers * 240} min capaciteit per dagdeel (240 min/poetser × 4u)
                        </p>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#0A1F44] mb-2">
                            Dagdeel
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                {
                                    v: "full",
                                    label: "Volledige dag",
                                    icon: <CalendarIcon size={14} />,
                                },
                                {
                                    v: "morning_only",
                                    label: "Enkel voormiddag",
                                    icon: <Sun size={14} />,
                                },
                                {
                                    v: "afternoon_only",
                                    label: "Enkel namiddag",
                                    icon: <Moon size={14} />,
                                },
                            ].map((opt) => (
                                <button
                                    key={opt.v}
                                    onClick={() => setSlotMode(opt.v)}
                                    className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border-2 transition-colors ${
                                        slotMode === opt.v
                                            ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                                            : "bg-white text-[#0A1F44] border-[#e5e5e5] hover:border-[#0A1F44]"
                                    }`}
                                    data-testid={`slotmode-${day.date}-${opt.v}`}
                                >
                                    {opt.icon}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <button
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-4 py-2 text-sm font-semibold hover:bg-[#d05c10] transition-colors disabled:opacity-60"
                            data-testid={`save-day-${day.date}`}
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Check size={14} />
                            )}
                            Opslaan
                        </button>
                        <button
                            onClick={cancelEdit}
                            disabled={saving}
                            className="inline-flex items-center gap-2 border-2 border-[#e5e5e5] text-[#0A1F44] px-4 py-2 text-sm font-semibold hover:border-[#0A1F44] transition-colors disabled:opacity-60"
                            data-testid={`cancel-day-${day.date}`}
                        >
                            Annuleren
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
